import { FC } from 'react'
import { useIntl } from 'react-intl'
import { createBrazilianDate } from '../../../../../_metronic/helpers/AssetHelpers'
import './PublicClient.css'

type Props = {
  beginDate?: string
  endDate?: string
  expectedDate?: string
  status?: string
}

const PublicDeadlineProgressCard: FC<Props> = ({
  beginDate,
  endDate,
  expectedDate,
  status,
}) => {
  const intl = useIntl()

  const getStatusInfo = () => {
    const now = new Date()
    const startDate = beginDate ? createBrazilianDate(beginDate) : null
    const projectEndDate = endDate ? createBrazilianDate(endDate) : null
    const deadlineDate = expectedDate ? createBrazilianDate(expectedDate) : null
    
    // Se o projeto foi concluído (baseado no status)
    if (status === 'Concluído') {
      const isLate = deadlineDate && projectEndDate && projectEndDate > deadlineDate
      return {
        status: isLate ? 'danger' : 'success',
        icon: isLate ? 'bi-calendar-x' : 'bi-calendar-check',
        text: isLate ? 'Concluído com atraso' : 'Concluído no prazo',
        color: isLate ? 'text-danger' : 'text-success'
      }
    }
    
    // Se não há prazo definido
    if (!deadlineDate) {
      return {
        status: 'secondary',
        icon: 'bi-calendar-range',
        text: 'Em andamento',
        color: 'text-muted'
      }
    }
    
    // Se já passou da data de prazo, está atrasado
    if (now > deadlineDate) {
      return {
        status: 'danger',
        icon: 'bi-exclamation-triangle-fill',
        text: 'Prazo expirado',
        color: 'text-danger'
      }
    }
    
    // Calcular percentual de progresso
    const daysSinceStart = startDate ? Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0
    const totalDays = Math.floor((deadlineDate.getTime() - (startDate || now).getTime()) / (1000 * 60 * 60 * 24))
    const progressPercent = totalDays > 0 ? Math.min(100, (daysSinceStart / totalDays) * 100) : 0
    
    if (progressPercent > 80) {
      return {
        status: 'warning',
        icon: 'bi-clock-fill',
        text: 'Próximo do prazo',
        color: 'text-warning'
      }
    } else {
      return {
        status: 'success',
        icon: 'bi-check-circle-fill',
        text: 'No prazo',
        color: 'text-success'
      }
    }
  }

  // Calcular métricas
  const getMetrics = () => {
    const now = new Date()
    const startDate = beginDate ? createBrazilianDate(beginDate) : null
    const projectEndDate = endDate ? createBrazilianDate(endDate) : null
    const deadlineDate = expectedDate ? createBrazilianDate(expectedDate) : null
    
    if (status === 'Concluído') {
      // Projeto concluído
      const daysSinceStart = startDate && projectEndDate ? Math.floor((projectEndDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0
      const totalDays = deadlineDate && startDate ? Math.floor((deadlineDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : daysSinceStart
      const progressPercent = totalDays > 0 ? Math.min(100, (daysSinceStart / totalDays) * 100) : 100
      
      return {
        daysElapsed: daysSinceStart,
        totalDays: totalDays,
        daysRemaining: 0,
        progressPercent: progressPercent
      }
    }
    
    if (!deadlineDate || !startDate) {
      // Sem prazo definido
      const daysSinceStart = startDate ? Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) : 0
      return {
        daysElapsed: daysSinceStart,
        totalDays: daysSinceStart || 1,
        daysRemaining: 0,
        progressPercent: 0
      }
    }
    
    // Projeto em andamento com prazo definido
    const daysSinceStart = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const totalDays = Math.floor((deadlineDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const daysRemaining = Math.max(0, totalDays - daysSinceStart)
    const progressPercent = totalDays > 0 ? Math.min(100, (daysSinceStart / totalDays) * 100) : 0
    
    return {
      daysElapsed: Math.max(0, daysSinceStart),
      totalDays: totalDays,
      daysRemaining: daysRemaining,
      progressPercent: progressPercent
    }
  }

  const statusInfo = getStatusInfo()
  const metrics = getMetrics()

  // Sem data prevista não há como medir progresso (antes mostrava 0% e "X/X dias", sem sentido).
  const hasDeadline = !!expectedDate || status === 'Concluído'

  return (
    <section className='pc-section'>
      <div className='pc-section-header'>
        <h2 className='pc-section-title'>Prazo do projeto</h2>
        {hasDeadline && (
          <span className={`badge badge-light-${statusInfo.status} fw-bolder`}>
            <i className={`${statusInfo.icon} me-1`} aria-hidden='true'></i>
            {statusInfo.text}
          </span>
        )}
      </div>

      {hasDeadline ? (
        <>
          <div className='pc-progress-row'>
            <span className='text-gray-700'>Progresso: {metrics.progressPercent.toFixed(0)}%</span>
            <span className={statusInfo.color}>
              {metrics.daysElapsed}/{metrics.totalDays} dias
            </span>
          </div>
          <progress
            className={`pc-progress pc-progress-${statusInfo.status}`}
            value={Math.round(metrics.progressPercent)}
            max={100}
            aria-label='Progresso do prazo do projeto'
          />

          <div className='pc-stats'>
            <div className='pc-stat'>
              <span className='pc-stat-value'>{metrics.daysElapsed}</span>
              <span className='pc-stat-label'>Decorridos</span>
            </div>
            <div className='pc-stat'>
              <span className='pc-stat-value'>{metrics.totalDays}</span>
              <span className='pc-stat-label'>Prazo total</span>
            </div>
            <div className='pc-stat'>
              <span className='pc-stat-value'>{metrics.daysRemaining}</span>
              <span className='pc-stat-label'>Restantes</span>
            </div>
          </div>
        </>
      ) : (
        <div className='pc-state'>
          <i className='bi bi-calendar-plus' aria-hidden='true'></i>
          <p className='pc-state-title'>Prazo não definido</p>
          <p className='mb-0'>Este projeto ainda não tem data prevista de entrega.</p>
        </div>
      )}
    </section>
  )
}

export { PublicDeadlineProgressCard }
